import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["triggerRef"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import ButtonGroup from '@atlaskit/button/button-group';
import Tooltip from '@atlaskit/tooltip';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { SmartLinkSize } from '../../../../../constants';
import Button from '@atlaskit/button/standard-button';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { sizeToButtonSpacing } from '../../utils';
import { messages } from '../../../../../messages';
import { useFlexibleUiContext, useFlexibleUiOptionContext } from '../../../../../state/flexible-ui-context';
import ActionGroupItem from './action-group-item';
import { filterActionItems } from '../utils';
import { di } from 'react-magnetic-di';
var styles = css({
  display: 'inline-flex',
  lineHeight: '1rem',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '> div': {
    alignItems: 'center',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    'button:focus-visible': {
      outlineOffset: "var(--ds-space-negative-025, -2px)"
    }
  }
});
var renderActionItems = function renderActionItems() {
  var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : SmartLinkSize.Medium;
  var appearance = arguments.length > 2 ? arguments[2] : undefined;
  var asDropDownItems = arguments.length > 3 ? arguments[3] : undefined;
  var onActionItemClick = arguments.length > 4 ? arguments[4] : undefined;
  return items.map(function (item, idx) {
    return jsx(ActionGroupItem, {
      item: item,
      key: idx,
      size: size,
      appearance: appearance,
      asDropDownItems: asDropDownItems,
      onActionItemClick: onActionItemClick
    });
  });
};

/**
 * Creates a group of Action components. Accepts an array of Actions, in addition to some styling
 * preferences.
 * @internal
 * @param {ActionGroupProps} ActionGroupProps
 * @see Action
 */
var ActionGroup = function ActionGroup(_ref) {
  var _ref$items = _ref.items,
    items = _ref$items === void 0 ? [] : _ref$items,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? SmartLinkSize.Medium : _ref$size,
    appearance = _ref.appearance,
    _ref$visibleButtonsNu = _ref.visibleButtonsNum,
    visibleButtonsNum = _ref$visibleButtonsNu === void 0 ? 2 : _ref$visibleButtonsNu,
    onDropdownOpenChange = _ref.onDropdownOpenChange;
  var context = useFlexibleUiContext();
  var ui = useFlexibleUiOptionContext();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var renderableActionItems = useMemo(function () {
    return filterActionItems(items, context);
  }, [context, items]);
  var isMoreThenTwoItems = renderableActionItems.length > visibleButtonsNum;
  var onOpenChange = useCallback(function (attrs) {
    setIsOpen(attrs.isOpen);
    if (onDropdownOpenChange) {
      onDropdownOpenChange(attrs.isOpen);
    }
  }, [onDropdownOpenChange]);
  var onActionItemClick = useCallback(function () {
    if (isOpen) {
      onOpenChange({
        isOpen: false
      });
    }
  }, [isOpen, onOpenChange]);
  var actionButtons = useMemo(function () {
    var actionItems = isMoreThenTwoItems ? renderableActionItems.slice(0, visibleButtonsNum - 1) : renderableActionItems;
    return renderActionItems(actionItems, size, appearance, false, onActionItemClick);
  }, [appearance, isMoreThenTwoItems, onActionItemClick, renderableActionItems, size, visibleButtonsNum]);
  var moreActionDropdown = useMemo(function () {
    var actionItems = isMoreThenTwoItems ? renderableActionItems.slice(visibleButtonsNum - 1) : [];
    if (actionItems.length > 0) {
      var spacing = sizeToButtonSpacing[size];
      var moreIcon = jsx(MoreIcon, {
        label: "more",
        color: "currentColor"
      });
      var formatMessage = jsx(FormattedMessage, messages.more_actions);
      return jsx(DropdownMenu, {
        isOpen: isOpen,
        onOpenChange: onOpenChange,
        trigger: function trigger(_ref2) {
          var triggerRef = _ref2.triggerRef,
            props = _objectWithoutProperties(_ref2, _excluded);
          return jsx(Tooltip, {
            content: formatMessage,
            hideTooltipOnClick: true,
            testId: "action-group-more-button-tooltip",
            tag: "span"
          }, jsx(Button, _extends({}, props, {
            spacing: spacing,
            testId: "action-group-more-button",
            iconBefore: moreIcon,
            ref: triggerRef
          })));
        },
        testId: "action-group-dropdown",
        zIndex: ui === null || ui === void 0 ? void 0 : ui.zIndex
      }, renderActionItems(actionItems, size, appearance, true, onActionItemClick));
    }
    return null;
  }, [appearance, isMoreThenTwoItems, isOpen, onActionItemClick, onOpenChange, renderableActionItems, size, ui === null || ui === void 0 ? void 0 : ui.zIndex, visibleButtonsNum]);
  return renderableActionItems.length > 0 ? jsx("div", {
    css: styles
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "actions-button-group",
    "data-action-open": isOpen
  }, jsx(ButtonGroup, null, actionButtons, moreActionDropdown)) : null;
};
export default ActionGroup;